
/**
 * SyncService - Maneja la coordinación entre pestañas y ventanas para evitar
 * llamadas duplicadas a la API y condiciones de carrera.
 */

// Generar un ID único para esta pestaña/ventana
const TAB_ID = Math.random().toString(36).substring(2, 11);

// Nombre del canal para locks
const LOCK_CHANNEL = 'modular_lock_channel';

interface LockRecord {
  taskId: string;
  holder: string;
  expiry: number;
}

/**
 * Intenta adquirir un bloqueo local (en el mismo navegador) para una tarea específica.
 * @param taskId Identificador único de la tarea (ej: 'transcribe_audio_123')
 * @param durationMs Duración del bloqueo en milisegundos
 * @returns true si se adquirió el bloqueo, false si ya está tomado
 */
export const acquireLocalLock = (taskId: string, durationMs: number = 5000): boolean => {
  const lockKey = `modular_lock_${taskId}`;
  const now = Date.now();
  
  try {
    const existing = localStorage.getItem(lockKey);
    if (existing) {
      const lock: LockRecord = JSON.parse(existing);
      if (now < lock.expiry && lock.holder !== TAB_ID) {
        return false; // Bloqueo activo por otra pestaña
      }
    }
    
    const newLock: LockRecord = {
      taskId,
      holder: TAB_ID,
      expiry: now + durationMs
    };
    
    localStorage.setItem(lockKey, JSON.stringify(newLock));
    
    // Notificar a otras pestañas (opcional, para UI)
    const bc = new BroadcastChannel(LOCK_CHANNEL);
    bc.postMessage({ type: 'LOCK_ACQUIRED', ...newLock });
    bc.close();
    
    return true;
  } catch (e) {
    console.warn("[SyncService] Error acquiring lock:", e);
    return true; // Fallback a permitir si el almacenamiento falla
  }
};

/**
 * Ejecuta una operación solo si esta pestaña logra adquirir el bloqueo.
 */
export const runOnce = async <T>(taskId: string, operation: () => Promise<T>, durationMs: number = 5000): Promise<T | null> => {
  if (acquireLocalLock(taskId, durationMs)) {
    console.log(`[SyncService] Adquiriendo liderazgo para tarea: ${taskId}`);
    return await operation();
  }
  console.log(`[SyncService] Tarea omitida (ya en proceso por otra ventana): ${taskId}`);
  return null;
};

/**
 * Libera un bloqueo de forma anticipada.
 */
export const releaseLocalLock = (taskId: string) => {
  const lockKey = `modular_lock_${taskId}`;
  localStorage.removeItem(lockKey);
  
  const bc = new BroadcastChannel(LOCK_CHANNEL);
  bc.postMessage({ type: 'LOCK_RELEASED', taskId, holder: TAB_ID });
  bc.close();
};

export { TAB_ID };
